import { ComponentProps, FC, ReactElement, useMemo } from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { RmText } from "../RmText/RmText";
import type { TextVariantType, TextWeightType } from "../RmText/variants";
import { buttonVariants } from "./variants";

type RmButtonIconPropsType = VariantProps<typeof buttonVariants> & {
  iconComponent?: ReactElement;
  isLoading: boolean;
};

const RmButtonIcon: FC<RmButtonIconPropsType> = ({
  isLoading,
  iconComponent,
}) => {
  switch (true) {
    case isLoading:
      return <Loader2 className="animate-spin" />;
    default:
      return iconComponent ?? null;
  }
};

type LoadingPositionType = "left" | "right";

export type RmButtonPropsType = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    textClassName?: ComponentProps<"text">["className"];
    textVariant?: TextVariantType;
    textWeight?: TextWeightType;
    asChild?: boolean;
    as?: React.ElementType;
    iconComponentRight?: ReactElement;
    iconComponentLeft?: ReactElement;
    isLoading?: boolean;
    isDisabled?: boolean;
    loadingPosition?: LoadingPositionType;
  } & ChildrenOrI18nType;

export const RmButton: FC<RmButtonPropsType> = ({
  className,
  textClassName,
  variant,
  size,
  textVariant,
  textWeight = "medium",
  asChild = false,
  as = "button",
  iconComponentRight,
  iconComponentLeft,
  isLoading = false,
  loadingPosition = "left",
  isDisabled = false,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const Comp = asChild ? Slot : as;

  const defaultTextVariants = size === "sm" ? "sm" : "md";

  // Load position based on iconComponent presence
  const loadingIconPosition = useMemo(() => {
    let result: LoadingPositionType = "left";

    if (Boolean(iconComponentRight) || loadingPosition === "right") {
      result = "right";
    }

    return result;
  }, [iconComponentRight, loadingPosition]);

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={isDisabled || isLoading}
      {...props}>
      {(iconComponentLeft ?? loadingIconPosition === "left") && (
        <RmButtonIcon isLoading={isLoading} iconComponent={iconComponentLeft} />
      )}
      {children ?? (
        <RmText
          as="span"
          className={textClassName}
          variant={textVariant ?? defaultTextVariants}
          weight={textWeight}
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
        />
      )}
      {(iconComponentRight ?? loadingIconPosition === "right") && (
        <RmButtonIcon
          isLoading={isLoading}
          iconComponent={iconComponentRight}
        />
      )}
    </Comp>
  );
};
